/**
 * 登入頁面 · factor chain 動態渲染（Task-tenant-login-overhaul PR G）
 *
 * step machine：username → (credential / captcha / totp) 依 tenant policy → [首次強制 enrollment]
 * → [backup code 顯示] → 導 return_url or /home。
 * 錯誤一律 400（showError，不跳 redirect）；session 過期回 username step。
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { useSystem } from '../contexts/SystemContext';
import { authService } from '../api/authService';
import { startLineLogin } from '../services/lineLoginService';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useIsMobile } from '../hooks/useIsMobile';
import { getI18nValue } from '../utils/i18nHelper';
import loginBgDefault from '../assets/images/login-bg.jpg';
import loginBg1 from '../assets/images/login-bg-1.jpg';
import loginBg2 from '../assets/images/login-bg-2.jpg';
import '../styles/LoginPage.css';

const LOGIN_BG_MAP: Record<string, string> = {
  default: loginBgDefault,
  nature: loginBg1,
  landscape: loginBg2,
};

type Step = 'login' | 'captcha' | 'totp' | 'enroll' | 'backup';

interface CaptchaData { key: string; svg: string; }

const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { systemProfile, getLoginBg, getMessageByCode, getSystemTitle, tenantBranding, getCopyright } = useSystem();
  const isMobile = useIsMobile();   // 手機/平板/PWA → 登入後導 PWA 行動版首頁
  // 租戶上傳的登入背景（file_object）優先；否則走既有代碼底圖。
  // 注意：下方 style 會再包一層 url(${bgImage})，故這裡回傳純路徑不自帶 url()。
  const bgImage = tenantBranding?.login_bg_file_id
    ? `/api/system/branding-image/${tenantBranding.login_bg_file_id}`
    : (LOGIN_BG_MAP[getLoginBg()] || LOGIN_BG_MAP['default']);

  const [step, setStep] = useState<Step>('login');
  const [username, setUsername] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [password, setPassword] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  // 首次強制 enrollment
  const [enrollSecret, setEnrollSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [enrollCode, setEnrollCode] = useState('');
  // backup code 顯示（enroll 完成後必經）
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [pendingToken, setPendingToken] = useState('');
  const [copied, setCopied] = useState(false);

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // TOTP UX 保護（porsche 2026-08-29）：30s window 剩餘秒數、rate-limit 冷卻倒數、過期 auto-focus
  const [totpRemaining, setTotpRemaining] = useState(30);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const totpInputRef = useRef<HTMLInputElement>(null);
  const enrollInputRef = useRef<HTMLInputElement>(null);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // return_url + trigger（zero-trust re-login 導回原頁）
  const [returnUrl, setReturnUrl] = useState<string | null>(null);
  const [trigger, setTrigger] = useState<string | null>(null);
  const [pendingMsg, setPendingMsg] =
    useState<{ code: string; params?: Record<string, string> } | null>(null);

  // 讀 URL query（只執行一次）
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    setReturnUrl(p.get('return'));
    const tr = p.get('trigger');
    setTrigger(tr);
    if (tr === 'idle_timeout') setInfo(t('auth.reloginIdle', '因長時間無操作已自動登出，請重新登入'));
    else if (tr === 'max_lifetime') setInfo(t('auth.reloginMaxLifetime', 'Session 已達最長時間限制，請重新登入'));
    else if (tr === 'forced_relogin') setInfo(t('auth.reloginForced', '基於安全政策需重新驗證，請重新登入'));

    const msg = p.get('msg');
    const paramsStr = p.get('p');
    if (msg) {
      let params: Record<string, string> | undefined;
      if (paramsStr) { try { params = JSON.parse(paramsStr); } catch { /* silent ok: URL msg param JSON 損毀 · 略過不 fatal */ } }
      setPendingMsg({ code: msg, params });
    }
    if (tr || msg || p.get('return')) window.history.replaceState({}, '', '/login');
  }, [t]);

  // messageCodes 載入後翻譯待顯示代碼
  useEffect(() => {
    if (pendingMsg) setError(getMessageByCode(pendingMsg.code, pendingMsg.params));
  }, [pendingMsg, getMessageByCode]);

  const title = getSystemTitle()
    || getI18nValue(systemProfile?.sys_title, i18n.language, 'Base AP Management System');

  // 統一錯誤處理：回 error_code（供 caller 分支）
  const showError = (err: any): string | null => {
    const detail = err?.response?.data?.detail;
    let code: string | null = null;
    if (typeof detail === 'string') { code = detail; setError(getMessageByCode(detail)); }
    else if (detail && typeof detail === 'object' && detail.code) {
      code = detail.code; setError(getMessageByCode(detail.code, detail.params));
    } else setError(err?.message || t('auth.loginFailed', '登入失敗'));
    return code;
  };

  const finish = (token: string) => {
    authService.saveToken(token);
    // ?return= 覆寫優先；否則依裝置：手機/平板/PWA → PWA 行動版首頁，桌機 → /home
    window.location.href = returnUrl || (isMobile ? '/ee_pwa_home' : '/home');
  };

  // Phase C1：依 host 查政策 + 管道，決定是否顯「以 LINE 登入」（L03 + line 啟用）
  const [lineEnabled, setLineEnabled] = useState(false);
  useEffect(() => {
    authService.getLoginConfig()
      .then((c) => setLineEnabled(c.policy_code === 'L03' && (c.tenant_channels || []).includes('line')))
      .catch(() => { /* silent ok: 政策查詢失敗不擋帳密登入 */ });
  }, []);

  const handleStartLineLogin = async () => {
    try {
      const url = await startLineLogin();
      window.location.href = url;   // 導向 LINE OAuth；回來走 /line-login-callback
    } catch (err) { showError(err); }  // lint-msg-allow: LoginPage 內部 helper · 解析 error_code 顯示
  };

  // 30s TOTP window 剩餘秒數（純 client · UTC 對齊，每 30s 邊界換碼）：totp / enroll 步驟才跑
  useEffect(() => {
    if (step !== 'totp' && step !== 'enroll') return;
    const tick = () => setTotpRemaining(30 - (Math.floor(Date.now() / 1000) % 30));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [step]);

  // rate-limit 冷卻倒數：讀 backend retry_after_seconds（未上線時 fallback 900），0 自動解鎖
  const startCooldown = useCallback((seconds?: number) => {
    const s = Math.max(0, Math.floor(Number(seconds) || 900));
    setCooldownSeconds(s);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    if (s <= 0) return;
    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // 卸載時清冷卻 timer
  useEffect(() => () => { if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current); }, []);

  const resetToStart = () => {
    setStep('login');
    setSessionId(''); setPassword(''); setCaptchaCode(''); setTotpCode('');
    setCaptcha(null); setUseBackupCode(false);
  };

  const refreshCaptcha = useCallback(async (sid: string) => {
    try {
      const d = await authService.refreshLoginCaptcha(sid);
      setCaptcha({ key: d.captcha_key, svg: d.captcha_svg });
      setCaptchaCode('');
    } catch (err) {
      // 不再 silent（porsche 2026-08-25「按鈕無法生效」感）：失敗顯錯誤，
      // 解不到 error_code 時退回通用「刷新失敗」提示；login session 過期則回起點。
      const code = showError(err);   // lint-msg-allow: LoginPage 內部 helper · 帶 code return
      if (code && code.endsWith('0011')) resetToStart();
      else if (!code) setError(t('auth.captchaRefreshFailed', '圖形碼刷新失敗，請重試或重新登入'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goEnroll = async (secret: string, uri: string) => {
    setEnrollSecret(secret);
    try { setQrDataUrl(await QRCode.toDataURL(uri, { width: 220, margin: 1 })); }
    catch { setQrDataUrl(''); }
    setStep('enroll');
  };

  // 依 factor response 決定下一步
  const applyResponse = (resp: any) => {
    // oneshot / factor 回的 session_id 都先存（L02/L03 續 factor、enroll 提交皆需要）
    if (resp?.session_id) setSessionId(resp.session_id);
    if (resp?.need_enrollment) { goEnroll(resp.secret_b32, resp.provisioning_uri); return; }
    if (resp?.token) { finish(resp.token); return; }
    if (resp?.next_factor) {
      if (resp.prepare?.captcha_key) {
        setCaptcha({ key: resp.prepare.captcha_key, svg: resp.prepare.captcha_svg });
      }
      setCaptchaCode(''); setTotpCode('');
      setStep(resp.next_factor);
    }
  };

  // 一次帳密登入（porsche UX）：帳密同送 → oneshot → L01 直進系統 / L02·L03 進下一 step / 未 enroll 進 QR
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      const resp = await authService.loginOneshot({ username, password, return_url: returnUrl, trigger });
      applyResponse(resp);
    } catch (err) {
      const code = showError(err); // lint-msg-allow: LoginPage 內部 helper · 帶 code return
      if (code && code.endsWith('0011')) resetToStart();   // login session 過期 → 重來
      else if (code && code.endsWith('0012')) startCooldown((err as any)?.response?.data?.detail?.retry_after_seconds);  // rate limit
    } finally { setIsLoading(false); }
  };

  const handleFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      let resp: any;
      if (step === 'captcha') {
        resp = await authService.loginFactorCaptcha({
          session_id: sessionId, captcha_key: captcha?.key || '', captcha_code: captchaCode,
        });
      } else if (step === 'totp') {
        resp = await authService.loginFactorTotp({ session_id: sessionId, code: totpCode });
      }
      applyResponse(resp);
    } catch (err) {
      const code = showError(err); // lint-msg-allow: LoginPage 內部 helper · 帶 code return
      // renumber 後：captcha 錯=ERR010009、session 過期=ERR010011、過期=ERR010023、rate limit=ERR010012。
      // 用 endsWith 比訊息尾碼（dev 環境 01→51 remap，尾碼不變）。
      if (code && code.endsWith('0009')) refreshCaptcha(sessionId);   // captcha 錯 → refresh
      if (code && code.endsWith('0011')) resetToStart();               // login session 過期 → 重來
      if (code && code.endsWith('0023')) { setTotpCode(''); totpInputRef.current?.focus(); }  // 過期 → 清空 + focus（showError 已顯訊息）
      if (code && code.endsWith('0012')) startCooldown((err as any)?.response?.data?.detail?.retry_after_seconds);  // rate limit
    } finally { setIsLoading(false); }
  };

  const handleEnrollConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      const resp = await authService.loginFactorTotpEnroll({ session_id: sessionId, code: enrollCode });
      setBackupCodes(resp.backup_codes || []);
      setPendingToken(resp.token);
      setStep('backup');
    } catch (err) {
      const code = showError(err); // lint-msg-allow: LoginPage 內部 helper · 帶 code return
      // login session 過期=ERR010011 / enrollment pending 過期=ERR010015 → 回起點
      if ((code && code.endsWith('0011')) || (code && code.endsWith('0015'))) resetToStart();
      // 認證碼已過期（網路慢常見，ERR010023）→ 清空 + focus 讓使用者立刻輸新碼（showError 已顯訊息）
      else if (code && code.endsWith('0023')) { setEnrollCode(''); enrollInputRef.current?.focus(); }
    } finally { setIsLoading(false); }
  };

  const copyBackup = () => {
    try { navigator.clipboard?.writeText(backupCodes.join('\n')); setCopied(true); } catch { /* silent ok: clipboard 權限被拒 · 用戶可手動複製 · 不 fatal */ }
  };

  // rate-limit 冷卻中：disable 所有 submit + 顯 mm:ss
  const locked = cooldownSeconds > 0;
  const cooldownMmss = `${Math.floor(cooldownSeconds / 60).toString().padStart(2, '0')}:${(cooldownSeconds % 60).toString().padStart(2, '0')}`;

  // TOTP 30s 剩餘提示（totp / enroll 共用）：≤ 5s 橘字警告 + 建議等下組
  const renderTotpRemaining = () => (
    <p className="totp-remaining" style={{ color: totpRemaining <= 5 ? '#d97706' : '#666' }}>
      {t('auth.totpRemaining', '本組驗證碼剩餘 {{seconds}} 秒', { seconds: totpRemaining })}
      {totpRemaining <= 5 && ` ${t('auth.totpRemainingSoon', '（建議等下組）')}`}
    </p>
  );

  // ── 各 step 的表單 ─────────────────────────────────────────────────────────
  // 帳密同一 form（porsche UX）：一次送 account + password → oneshot
  const renderLogin = () => (
    <form onSubmit={handleLoginSubmit} className="login-form">
      <div className="form-group">
        <label htmlFor="account">{t('auth.account', '帳號')}</label>
        <input type="text" id="account" name="account" value={username} autoFocus
               onChange={(e) => { setUsername(e.target.value); setError(''); }}
               placeholder={t('auth.accountPlaceholder', '請輸入帳號')}
               required disabled={isLoading} autoComplete="username" />
      </div>
      <div className="form-group">
        <label htmlFor="password">{t('auth.password', '密碼')}</label>
        <div className="password-input-wrapper">
          <input type={showPassword ? 'text' : 'password'} id="password" name="password"
                 value={password}
                 onChange={(e) => { setPassword(e.target.value); setError(''); }}
                 placeholder={t('auth.passwordPlaceholder', '請輸入密碼')}
                 required disabled={isLoading} autoComplete="current-password" />
          <button type="button" className="password-toggle-btn" tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t('auth.hidePassword', '隱藏密碼') : t('auth.showPassword', '顯示密碼')}>
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
      </div>
      <button type="submit" className="login-button" disabled={isLoading || locked || !username || !password}>
        {isLoading ? t('auth.loggingIn', '登入中…') : t('auth.loginButton', '登入')}
      </button>
      {/* Phase C1：租戶 L03 + 啟用 line 管道才顯「以 LINE 登入」（每次 LINE 登入強制 TOTP）*/}
      {lineEnabled && (
        <>
          <div className="login-divider" style={{ textAlign: 'center', color: '#999', margin: '12px 0' }}>
            —— {t('common.or', '或')} ——
          </div>
          <button type="button" className="login-button" style={{ background: '#06c755' }}
                  disabled={isLoading} onClick={handleStartLineLogin}>
            {t('lineLogin.loginButton', '以 LINE 登入')}
          </button>
        </>
      )}
    </form>
  );

  const renderCaptcha = () => (
    <form onSubmit={handleFactorSubmit} className="login-form">
      {/* v4：input 對照其他 form-group 全寬一行；SVG/按鈕/提示對照獨立一行 · row 排（porsche 2026-08-29 截圖） */}
      <div className="form-group">
        <label>{t('auth.captcha', '驗證碼')}</label>
        <input type="text" name="captcha_code" value={captchaCode} autoFocus
               onChange={(e) => { setCaptchaCode(e.target.value); setError(''); }}
               placeholder={t('auth.captchaPlaceholder', '請輸入驗證碼')}
               required disabled={isLoading} autoComplete="off" maxLength={4}
               className="captcha-input" />
      </div>
      <div className="captcha-refresh-row">
        <div className="captcha-image" dangerouslySetInnerHTML={{ __html: captcha?.svg || '' }}
             onClick={() => refreshCaptcha(sessionId)}
             title={t('auth.refreshCaptcha', '點擊更新驗證碼')} />
        <button type="button" className="captcha-refresh-btn"
                onClick={() => refreshCaptcha(sessionId)} disabled={isLoading}
                title={t('auth.refreshCaptchaTooltip', '重新產生圖形碼')}
                aria-label={t('auth.refreshCaptchaAria', '重新產生圖形碼')}>
          🔄 {t('auth.refreshCaptchaLabel', '更新')}
        </button>
        <div className="captcha-hint">
          {t('auth.captchaHint', '看不清楚？點圖形或按「更新」重新產生一組')}
        </div>
      </div>
      <button type="submit" className="login-button" disabled={isLoading || locked}>
        {isLoading ? t('auth.loggingIn', '登入中…') : t('auth.loginButton', '登入')}
      </button>
    </form>
  );

  const renderTotp = () => (
    <form onSubmit={handleFactorSubmit} className="login-form">
      <div className="form-group">
        <label>{useBackupCode
          ? t('auth.backupCodePrompt', '請輸入備援碼（8 字元）')
          : t('auth.totpPrompt', '請輸入 Authenticator 顯示的 6 位數')}</label>
        <input type="text" name="totp" value={totpCode} autoFocus inputMode="text"
               ref={totpInputRef}
               onChange={(e) => { setTotpCode(e.target.value.trim()); setError(''); }}
               placeholder={useBackupCode ? 'XXXXXXXX' : '000000'}
               required disabled={isLoading || locked} autoComplete="one-time-code"
               maxLength={useBackupCode ? 8 : 6} className="captcha-input" />
      </div>
      {!useBackupCode && renderTotpRemaining()}
      <button type="button" className="link-button"
              onClick={() => { setUseBackupCode(!useBackupCode); setTotpCode(''); setError(''); }}>
        {useBackupCode ? t('auth.useTotp', '改用 Authenticator 驗證碼') : t('auth.useBackupCode', '使用備援碼')}
      </button>
      <button type="submit" className="login-button" disabled={isLoading || locked}>
        {isLoading ? t('auth.loggingIn', '登入中…') : t('auth.loginButton', '登入')}
      </button>
    </form>
  );

  const renderEnroll = () => (
    <form onSubmit={handleEnrollConfirm} className="login-form">
      <p className="enroll-title">{t('auth.enrollTitle', '首次登入 · 請啟用兩步驟驗證')}</p>
      {qrDataUrl
        ? <>
            <img src={qrDataUrl} alt="TOTP QR" className="enroll-qr" />
            <p className="qr-hint-fresh">{t('auth.qrHintFresh', '掃描此組 QR 完成綁定')}</p>
          </>
        : <p className="enroll-hint">{t('auth.enrollQrFailed', 'QR 產生失敗，請用下方密鑰手動輸入')}</p>}
      <p className="enroll-hint">
        {t('auth.enrollManualKey', '若掃碼有問題，可手動輸入密鑰：')}<br />
        <code className="enroll-secret">{enrollSecret}</code>
      </p>
      <div className="form-group">
        <label>{t('auth.totpPrompt', '請輸入 Authenticator 顯示的 6 位數')}</label>
        <input type="text" name="enrollCode" value={enrollCode} autoFocus inputMode="numeric"
               ref={enrollInputRef}
               onChange={(e) => { setEnrollCode(e.target.value.trim()); setError(''); }}
               placeholder="000000" required disabled={isLoading} autoComplete="one-time-code"
               maxLength={6} className="captcha-input" />
      </div>
      {renderTotpRemaining()}
      <button type="submit" className="login-button" disabled={isLoading}>
        {isLoading ? t('auth.loggingIn', '啟用中…') : t('auth.enrollConfirm', '確認啟用')}
      </button>
    </form>
  );

  const renderBackup = () => (
    <div className="login-form">
      <p className="enroll-title">{t('auth.backupTitle', '請保存備援碼')}</p>
      <p className="enroll-hint">
        {t('auth.backupNote', '每組 8 字元、只能用一次，且只顯示這一次。請立即列印或存入密碼管理器。')}
      </p>
      <div className="backup-codes">
        {backupCodes.map((c) => <code key={c} className="backup-code">{c}</code>)}
      </div>
      <div className="backup-actions">
        <button type="button" className="link-button" onClick={copyBackup}>
          {copied ? t('auth.copied', '已複製') : t('auth.copyAll', '全部複製')}
        </button>
        <button type="button" className="link-button" onClick={() => window.print()}>
          {t('auth.print', '列印')}
        </button>
      </div>
      <button type="button" className="login-button" onClick={() => finish(pendingToken)}>
        {t('auth.backupSaved', '我已保存 · 繼續')}
      </button>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'captcha': return renderCaptcha();
      case 'totp': return renderTotp();
      case 'enroll': return renderEnroll();
      case 'backup': return renderBackup();
      default: return renderLogin();
    }
  };

  const formTitle = step === 'enroll' || step === 'backup'
    ? t('auth.twoFactor', '兩步驟驗證')
    : t('auth.login', '登入');

  return (
    <div className="login-page" style={{ '--login-bg-image': `url(${bgImage})` } as React.CSSProperties}>
      <div className="login-container">
        <div className="header-lang-switcher">
          <LanguageSwitcher variant="dark" />
        </div>

        <div className="login-header">
          <h1 className={`system-title ${i18n.language === 'en' ? 'lang-en' : 'lang-zh'}`}>
            {title}
          </h1>
        </div>

        <div className="login-form-container">
          <h3 className="login-form-title">{formTitle}</h3>
          {username && step !== 'login' && (
            <p className="login-username-hint">{username}</p>
          )}

          {info && <div className="info-message"><span>ℹ️</span> {info}</div>}
          {error && <div className="error-message"><span>⚠️</span> {error}</div>}
          {locked && (
            <div className="rate-limit-cooldown">
              ⏳ {t('auth.rateLimitCooldown', '登入嘗試次數過多 · 請等待 {{mmss}} 後重試', { mmss: cooldownMmss })}
            </div>
          )}

          {renderStep()}
        </div>

        <div className="login-footer">
          <p>
            {/* 租戶上傳 Logo(file_object)優先，否則平台預設 /logo.png（對齊 MainLayout #13）*/}
            <img
              src={tenantBranding?.logo_file_id
                ? `/api/system/branding-image/${tenantBranding.logo_file_id}`
                : '/logo.png'}
              alt="Logo" className="login-logo" />
            {/* tenant_copyright 優先，fallback sys_copyright（getCopyright() 已封裝該邏輯）*/}
            {getCopyright() || t('common.copyright', '')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
